// Species step component - Step 2 of character creation wizard

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { getChoiceLabel, isOriginFeatChoice, renderMarkdown, slugToTitle } from '../../lib/format.js';
import './option-card.js';
import './skill-option-card.js';
import './spell-option-card.js';
import './feat-choice-picker.js';

/**
 * @typedef {import('../views/create-character-view.js').SpeciesListItem} SpeciesListItem
 * @typedef {import('../views/create-character-view.js').SpeciesCreationOptions} SpeciesCreationOptions
 * @typedef {import('../views/create-character-view.js').FeatCreationChoices} FeatCreationChoices
 * @typedef {import('../views/create-character-view.js').OriginFeatItem} OriginFeatItem
 * @typedef {import('../../types/jsdoc-types.js').ChoiceOption} ChoiceOption
 * @typedef {import('../../types/jsdoc-types.js').FeatureChoice} FeatureChoice
 */

// Available languages (excluding Common which is granted)
const AVAILABLE_LANGUAGES = [
  { slug: 'common-sign-language', name: 'Common Sign Language' },
  { slug: 'draconic', name: 'Draconic' },
  { slug: 'dwarvish', name: 'Dwarvish' },
  { slug: 'elvish', name: 'Elvish' },
  { slug: 'giant', name: 'Giant' },
  { slug: 'gnomish', name: 'Gnomish' },
  { slug: 'goblin', name: 'Goblin' },
  { slug: 'halfling', name: 'Halfling' },
  { slug: 'orc', name: 'Orc' },
];

/**
 * Species selection step component.
 * Controlled component: receives all data as properties, emits events for mutations.
 *
 * Events: select-species, select-lineage, select-trait-choice,
 * select-origin-feat-choice, toggle-origin-feat-spell, toggle-language, select-size
 */
class SpeciesStep extends BaseComponent {
  static properties = {
    speciesList: { type: Array },
    selectedSlug: { type: String, attribute: 'selected-slug' },
    speciesOptions: { type: Object },
    originFeats: { type: Array },
    originFeatOptions: { type: Object },
    speciesSize: { type: String, attribute: 'species-size' },
    speciesLineage: { type: String, attribute: 'species-lineage' },
    speciesTraitChoices: { type: Object },
    originFeatChoices: { type: Object },
    languages: { type: Array },
    alreadyChosenSkills: { type: Object },
    alreadyChosenSpells: { type: Object },
    backgroundMagicInitiateSpellList: { type: String, attribute: 'background-magic-initiate-spell-list' },
    isHeaderStuck: { type: Boolean, attribute: 'is-header-stuck' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .selectable-card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    .selectable-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .selectable-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }

    .option-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 0.5rem;
    }

    .language-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 0.5rem;
    }
  `;

  constructor() {
    super();
    /** @type {Array<SpeciesListItem>} */
    this.speciesList = [];
    this.selectedSlug = '';
    /** @type {SpeciesCreationOptions | null} */
    this.speciesOptions = null;
    /** @type {Array<OriginFeatItem>} */
    this.originFeats = [];
    /** @type {FeatCreationChoices | null} */
    this.originFeatOptions = null;
    this.speciesSize = '';
    this.speciesLineage = '';
    /** @type {{[key: string]: string}} */
    this.speciesTraitChoices = {};
    /** @type {{[key: string]: string}} */
    this.originFeatChoices = {};
    /** @type {Array<string>} */
    this.languages = [];
    /** @type {Set<string>} */
    this.alreadyChosenSkills = new Set();
    /** @type {Set<string>} */
    this.alreadyChosenSpells = new Set();
    this.backgroundMagicInitiateSpellList = '';
    this.isHeaderStuck = false;
  }

  render() {
    return html`
      <div>
        <div class="sticky-header ${this.isHeaderStuck ? 'stuck' : ''}">
          <h5 class="card-title mb-0">Choose Your Species</h5>
        </div>
        <p class="text-muted mb-3">Your species determines your physical characteristics and grants unique traits.</p>

        <div class="option-grid mb-4">
          ${this.speciesList.map(
            (sp) => html`
              <div
                class="card h-100 selectable-card ${this.selectedSlug === sp.slug ? 'selected' : ''}"
                @click="${() => this._onSelectSpecies(sp.slug)}"
              >
                <div class="card-body">
                  <h6 class="card-title mb-1">${sp.name}</h6>
                  <p class="card-text small text-muted mb-0">
                    ${sp.description?.substring(0, 100)}${(sp.description?.length ?? 0) > 100 ? '...' : ''}
                  </p>
                </div>
              </div>
            `,
          )}
        </div>

        ${this.speciesOptions ? this._renderSpeciesOptions() : ''}

        <!-- Languages -->
        ${
          this.speciesOptions
            ? html`
              <h6 class="mb-2 mt-4">
                Languages
                <span class="badge bg-primary ms-1">${this.languages.length} / 2</span>
              </h6>
              <p class="text-muted small mb-2">All characters know Common. Choose 2 additional languages.</p>
              <div class="language-grid mb-3">
                <option-card
                  name="Common"
                  slug="common"
                  input-type="checkbox"
                  ?selected="${true}"
                  ?disabled="${true}"
                ></option-card>
                ${AVAILABLE_LANGUAGES.map((lang) => {
                  const isSelected = this.languages.includes(lang.slug);
                  const isDisabled = !isSelected && this.languages.length >= 2;
                  const isRecommended = (this.speciesOptions?.species?.recommendedLanguages || []).includes(lang.slug);
                  return html`
                    <option-card
                      name="${lang.name}"
                      slug="${lang.slug}"
                      input-type="checkbox"
                      ?selected="${isSelected}"
                      ?disabled="${isDisabled}"
                      ?recommended="${isRecommended}"
                      @card-selected="${() => this._onToggleLanguage(lang.slug)}"
                    ></option-card>
                  `;
                })}
              </div>
            `
            : ''
        }
      </div>
    `;
  }

  _renderSpeciesOptions() {
    const options = this.speciesOptions;
    if (!options) return html``;

    const lineages = options.lineages ? Object.entries(options.lineages) : [];
    const selectedLineageData = this.speciesLineage && options.lineages ? options.lineages[this.speciesLineage] : null;

    return html`
      <h5 class="mt-4 mb-3">Traits</h5>

      <div class="mb-4">
        <h6 class="mb-2">Creature Type</h6>
        <p class="text-muted small mb-0">
          You are a <span class="text-capitalize">${options.species.creatureType}</span>.
        </p>
      </div>

      <!-- Size -->
      <div class="mb-4">
        <h6 class="mb-2">
          Size
          ${
            options.species.size.length > 1
              ? html`<span class="badge bg-primary ms-1">${this.speciesSize ? '1' : '0'} / 1</span>`
              : ''
          }
        </h6>
        ${
          options.species.size.length > 1
            ? html`
              <p class="text-muted small mb-2">Choose your character's size.</p>
              <div class="row g-2">
                ${options.species.size.map(
                  (size) => html`
                    <div class="col-auto">
                      <option-card
                        name="${slugToTitle(size)}"
                        slug="${size}"
                        ?selected="${this.speciesSize === size}"
                        input-type="radio"
                        radio-group="species-size"
                        @card-selected="${() => this._onSelectSize(size)}"
                      ></option-card>
                    </div>
                  `,
                )}
              </div>
            `
            : html`
              <p class="text-muted small mb-0">
                You are <span class="text-capitalize">${options.species.size[0]}</span>.
              </p>
            `
        }
      </div>

      <div class="mb-4">
        <h6 class="mb-2">Speed</h6>
        <p class="text-muted small mb-0">Your base walking speed is ${options.species.speed} feet.</p>
      </div>

      <!-- Lineage Selection -->
      ${
        lineages.length > 0
          ? html`
            <div class="mb-4">
              <h6 class="mb-2">
                Lineage
                <span class="badge bg-primary ms-1">${this.speciesLineage ? '1' : '0'} / 1</span>
              </h6>
              <div class="option-grid">
                ${lineages.map(
                  ([slug, lineage]) => html`
                    <div
                      class="card h-100 selectable-card ${this.speciesLineage === slug ? 'selected' : ''}"
                      @click="${() => this._onSelectLineage(slug)}"
                    >
                      <div class="card-body">
                        <h6 class="card-title mb-1">${lineage.name || slug}</h6>
                        ${
                          lineage.description
                            ? html`<p class="card-text small text-muted mb-0">${lineage.description}</p>`
                            : ''
                        }
                      </div>
                    </div>
                  `,
                )}
              </div>
            </div>

            <!-- Lineage Benefits -->
            ${
              selectedLineageData
                ? html`
                  ${selectedLineageData.benefits?.map(
                    (benefit) => html`
                      <div class="mb-4">
                        <h6 class="mb-2">${benefit.name}</h6>
                        <div class="text-muted small mb-0">${renderMarkdown(benefit.description)}</div>
                      </div>
                    `,
                  )}

                  <!-- Lineage Choices -->
                  ${selectedLineageData.choices?.map((choice) => {
                    const choiceId = choice.id || '';
                    return html`
                      <div class="mb-4">
                        <h6 class="mb-2">
                          ${getChoiceLabel(choice) || choiceId}
                          <span class="badge bg-primary ms-2"
                            >${this.speciesTraitChoices[`lineage.${choiceId}`] ? 1 : 0} / 1</span
                          >
                        </h6>
                        ${
                          choice.description
                            ? html`<div class="text-muted small mb-2">${renderMarkdown(choice.description)}</div>`
                            : ''
                        }
                        <div class="row g-2">
                          ${(Array.isArray(choice.options) ? choice.options : []).map(
                            (/** @type {ChoiceOption|string} */ option) => {
                              const slug = typeof option === 'string' ? option : option.slug;
                              const name =
                                typeof option === 'string' ? slugToTitle(option) : option.name || option.slug;
                              const alreadyChosen = choice.choiceType === 'spell' && this.alreadyChosenSpells.has(slug);
                              const isSelected =
                                alreadyChosen || this.speciesTraitChoices[`lineage.${choiceId}`] === slug;
                              const optObj = /** @type {ChoiceOption} */ (option);
                              return html`
                                <div class="${choice.choiceType === 'spell' ? 'col-12 col-lg-6' : 'col-auto'}">
                                  ${
                                    choice.choiceType === 'spell'
                                      ? html`
                                        <spell-option-card
                                          name="${name}"
                                          slug="${slug}"
                                          school="${optObj.school || ''}"
                                          ?concentration="${optObj.concentration}"
                                          description="${optObj.description || ''}"
                                          ?selected="${isSelected}"
                                          ?disabled="${alreadyChosen}"
                                          ?known="${alreadyChosen}"
                                          input-type="radio"
                                          radio-group="lineage-${choiceId}"
                                          @card-selected="${(/** @type {CustomEvent} */ e) =>
                                            !alreadyChosen &&
                                            this._onSelectTraitChoice(`lineage.${choiceId}`, e.detail.slug)}"
                                        ></spell-option-card>
                                      `
                                      : html`
                                        <option-card
                                          name="${name}"
                                          slug="${slug}"
                                          ?selected="${isSelected}"
                                          input-type="radio"
                                          radio-group="lineage-${choiceId}"
                                          @card-selected="${(/** @type {CustomEvent} */ e) =>
                                            this._onSelectTraitChoice(`lineage.${choiceId}`, e.detail.slug)}"
                                        ></option-card>
                                      `
                                  }
                                </div>
                              `;
                            },
                          )}
                        </div>
                      </div>
                    `;
                  })}
                `
                : ''
            }
          `
          : ''
      }

      <!-- Special Traits -->
      ${options.specialTraits?.map(
        (trait) => html`
          <div class="mb-4">
            <h6 class="mb-2">${trait.name}</h6>
            <div class="text-muted small mb-2">${renderMarkdown(trait.description)}</div>

            <!-- Trait Choices -->
            ${trait.choices?.map((choice) => {
              const choiceId = choice.id || '';
              if (isOriginFeatChoice(choice)) {
                return html`
                  <div class="mb-3">
                    <span class="fst-italic">${getChoiceLabel(choice) || 'Origin Feat'}</span>
                    ${
                      choice.description
                        ? html`<div class="text-muted small mb-2">${renderMarkdown(choice.description)}</div>`
                        : ''
                    }
                    <div class="option-grid mt-1">
                      ${this.originFeats.map(
                        (feat) => html`
                          <div
                            class="card selectable-card h-100 ${
                              this.speciesTraitChoices[choiceId] === feat.slug ? 'selected' : ''
                            }"
                            @click="${() => this._onSelectTraitChoice(choiceId, feat.slug)}"
                          >
                            <div class="card-body py-2 px-3">
                              <div class="d-flex align-items-start">
                                <input
                                  type="radio"
                                  class="form-check-input me-2 mt-1"
                                  .checked="${this.speciesTraitChoices[choiceId] === feat.slug}"
                                  name="trait-${choiceId}"
                                  @click="${(/** @type {Event} */ e) => {
                                    e.stopPropagation();
                                    this._onSelectTraitChoice(choiceId, feat.slug);
                                  }}"
                                />
                                <div class="flex-grow-1">
                                  <span class="fw-medium">${feat.name}</span>
                                  ${
                                    feat.description
                                      ? html`<div class="text-muted small">${renderMarkdown(feat.description)}</div>`
                                      : ''
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        `,
                      )}
                    </div>

                    <!-- Origin feat choices -->
                    ${
                      this.originFeatOptions?.choices
                        ? html`
                          <div class="mt-3 ms-3">
                            <feat-choice-picker
                              .choices="${this.originFeatOptions.choices}"
                              .selections="${this.originFeatChoices}"
                              .alreadyChosenSpells="${this.alreadyChosenSpells}"
                              excluded-spell-list="${this.backgroundMagicInitiateSpellList || ''}"
                              radio-prefix="ofeat"
                              italic-labels
                              use-markdown
                              @choice-selected="${(/** @type {CustomEvent} */ e) =>
                                this._onSelectOriginFeatChoice(e.detail.choiceId, e.detail.value)}"
                              @spell-toggled="${(/** @type {CustomEvent} */ e) =>
                                this._onToggleOriginFeatSpell(e.detail.choiceId, e.detail.spellSlug)}"
                            ></feat-choice-picker>
                          </div>
                        `
                        : ''
                    }
                  </div>
                `;
              }

              // Regular trait choice
              return this._renderTraitChoice(choice, choiceId);
            })}
          </div>
        `,
      )}
    `;
  }

  /**
   * @param {any} choice
   * @param {string} choiceId
   */
  _renderTraitChoice(choice, choiceId) {
    return html`
      <div class="mb-3">
        <span class="fst-italic"
          >${getChoiceLabel(choice) || choiceId}
          <span class="badge bg-primary ms-2">${this.speciesTraitChoices[choiceId] ? 1 : 0} / 1</span></span
        >
        ${
          choice.description ? html`<div class="text-muted small mb-2">${renderMarkdown(choice.description)}</div>` : ''
        }
        <div class="${choice.choiceType === 'skill' ? 'option-grid' : 'row g-2'} mt-1">
          ${(Array.isArray(choice.options) ? choice.options : []).map((/** @type {ChoiceOption|string} */ option) => {
            const slug = typeof option === 'string' ? option : option.slug;
            const name = typeof option === 'string' ? slugToTitle(option) : option.name || option.slug;
            const ability = typeof option !== 'string' ? option.ability : '';
            const alreadyChosen =
              (choice.choiceType === 'skill' && this.alreadyChosenSkills.has(slug)) ||
              (choice.choiceType === 'spell' && this.alreadyChosenSpells.has(slug));
            const isSelected = alreadyChosen || this.speciesTraitChoices[choiceId] === slug;
            return choice.choiceType === 'skill'
              ? html`
                  <skill-option-card
                    name="${name}"
                    slug="${slug}"
                    ability="${ability}"
                    ?selected="${isSelected}"
                    ?disabled="${alreadyChosen}"
                    ?known="${alreadyChosen}"
                    input-type="radio"
                    radio-group="trait-${choiceId}"
                    @card-selected="${(/** @type {CustomEvent} */ e) =>
                      !alreadyChosen && this._onSelectTraitChoice(choiceId, e.detail.slug)}"
                  ></skill-option-card>
                `
              : html`
                  <div class="col-auto">
                    <option-card
                      name="${name}"
                      slug="${slug}"
                      ?selected="${isSelected}"
                      ?disabled="${alreadyChosen}"
                      ?known="${alreadyChosen}"
                      input-type="radio"
                      radio-group="trait-${choiceId}"
                      @card-selected="${(/** @type {CustomEvent} */ e) =>
                        !alreadyChosen && this._onSelectTraitChoice(choiceId, e.detail.slug)}"
                    ></option-card>
                  </div>
                `;
          })}
        </div>
      </div>
    `;
  }

  /** @param {string} slug */
  _onSelectSpecies(slug) {
    this.dispatchEvent(new CustomEvent('select-species', { detail: { slug }, bubbles: true, composed: true }));
  }

  /** @param {string} slug */
  _onSelectLineage(slug) {
    this.dispatchEvent(new CustomEvent('select-lineage', { detail: { slug }, bubbles: true, composed: true }));
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  _onSelectTraitChoice(key, value) {
    this.dispatchEvent(
      new CustomEvent('select-trait-choice', { detail: { key, value }, bubbles: true, composed: true }),
    );
  }

  /**
   * @param {string} choiceId
   * @param {string} value
   */
  _onSelectOriginFeatChoice(choiceId, value) {
    this.dispatchEvent(
      new CustomEvent('select-origin-feat-choice', { detail: { choiceId, value }, bubbles: true, composed: true }),
    );
  }

  /**
   * @param {string} choiceId
   * @param {string} spellSlug
   */
  _onToggleOriginFeatSpell(choiceId, spellSlug) {
    this.dispatchEvent(
      new CustomEvent('toggle-origin-feat-spell', { detail: { choiceId, spellSlug }, bubbles: true, composed: true }),
    );
  }

  /** @param {string} slug */
  _onToggleLanguage(slug) {
    this.dispatchEvent(new CustomEvent('toggle-language', { detail: { slug }, bubbles: true, composed: true }));
  }

  /** @param {string} size */
  _onSelectSize(size) {
    this.dispatchEvent(new CustomEvent('select-size', { detail: { size }, bubbles: true, composed: true }));
  }
}

customElements.define('species-step', SpeciesStep);
